import React, { Component } from "react";
import { Link } from "react-router-dom";
import qs from "qs";
import { AxiosResponse } from "axios";
import { QuestionPreview } from "../../../../stores/CoursePreviewStore/Types";
import { Answer, Enrolment } from "../../../../stores/EnrolmentStore/Types";
import { getRequest } from "../../../../utils/httpRequest";
import { errorNotification } from "../../../../utils/helpers";
import { CommentResource } from "../../../../stores/CommentStore/Types";
import AnswerUpvote from "./AnswerUpvote";
import AnswerStateModal from "../Assignment/AnswerStateModal";
import Assignment from "../Assignment";
import Discussion from "../Discussion";
import Conversations from "../Conversations";
import PersonalLessonSummary from "../PersonalLessonSummary";
import { nextLessonPath } from "../../helpers";

interface Props {
  enrolment: Enrolment;
  enrolmentId: number;
  lesson: QuestionPreview;
  lastLesson: QuestionPreview;
  lessonId: number;
  answerId: number;
  page: string;
  answer: Answer;
  refreshAnswers: () => void;
  getRecentNotifications: () => void;
  getAnswer: () => void;
  mode: string;
  loadingLesson: boolean;
  loadingAnswer: boolean;
  getXp: () => void;
  currentUser: any;
  search: string;
  embedded?: boolean;
}

interface State {
  answerState: string;
  sortBy: string;
  answers: any;
  // What the server says this enrolment has picked, across every page.
  chosen: any;
  comments: CommentResource[];
  voting: boolean;
  loading: boolean;
  showModal: boolean;
  page: number;
  hasMore: boolean;
  prev: number;
  next: number;
  imageUrl: string;
  enrolment: Enrolment;
  answerStateTagline: string;
  loadingComments: boolean;
  sortMenuOpen: boolean;
}

const passedTaglines = [
  "BRAVO!",
  "MARVELLOUS!",
  "KEEP IT UP",
  "JOLLY GOOD",
  "WONDERFUL",
  "FANTASTIC",
  "KEEP GOING",
  "KEEP UP THE GOOD WORK",
  "YOU GOT THIS",
  "NICE ONE",
  "SUPERB",
  "REMARKABLE",
];

const failedTaglines = [
  "OOPS!",
  "SO CLOSE!",
  "WHOOPSIE DAISY",
  "D'OH!",
  "TRY AGAIN",
  "HOUSTON, WE HAVE A PROBLEM!",
  "KEEP CALM AND CARRY ON",
];

class AnswerUpvotes extends Component<Props, State> {
  static defaultProps = { mode: "default" };
  sortRef: React.RefObject<HTMLDivElement>;
  answerUpvotesRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.sortRef = React.createRef();
    this.answerUpvotesRef = React.createRef();

    this.state = {
      answerState: "",
      sortBy: "random",
      answers: [],
      chosen: [],
      comments: [],
      voting: false,
      loading: true,
      showModal: false,
      page: 1,
      hasMore: true,
      prev: null,
      next: null,
      imageUrl: "",
      answerStateTagline: "",
      enrolment: props.enrolment,
      loadingComments: true,
      sortMenuOpen: false,
    };
  }

  componentDidMount() {
    this.scrollToAnswerUpvotes();
    document.addEventListener("mousedown", this.handleDocumentClick);
    this.getAnswer();
    this.fetchAnswers(this.state.sortBy);
    this.fetchComments();
    this.props.getRecentNotifications();
  }

  componentDidUpdate(prevProps: Props) {
    const canGetAnswer = this.props.enrolment?.id;
    const enrolmentLoaded = !prevProps.enrolment?.id && canGetAnswer;
    const answerChanged = prevProps.answerId !== this.props.answerId;
    const lessonChanged = prevProps.lessonId !== this.props.lessonId;

    if (canGetAnswer && (enrolmentLoaded || answerChanged || lessonChanged)) {
      this.getAnswer();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentClick);
  }

  getAnswer = () => {
    if (!this.props.enrolment?.id) {
      return;
    }

    this.props.getAnswer();
  };

  static getDerivedStateFromProps(props, state) {
    if (props.enrolment?.id !== state.enrolment?.id) {
      return {
        enrolment: props.enrolment,
      };
    }
    return null;
  }

  fetchAnswers = (sortBy) => {
    const { enrolmentId, answerId, lessonId, search } = this.props;
    const queryParams = qs.parse(search, { ignoreQueryPrefix: true });

    return getRequest(
      `/api/enrolments/${enrolmentId}/questions/${lessonId}/answer/answer_upvotes?answer_id=${answerId}&sort_by=${sortBy}&target_answer_id=${queryParams?.target_answer_id}`,
    )
      .then((response: AxiosResponse) => {
        this.setState({
          answers: response.data.answers,
          chosen: response.data.chosen || [],
          hasMore: response.data.has_more,
          prev: response.data.prev,
          next: response.data.next,
          page: response.data.page,
          loading: false,
        });
      })
      .catch((error) => {
        this.setState({ loading: false });
        errorNotification(
          "Error loading lesson",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  changeSortBy = (order) => {
    this.setState({ sortBy: order });
  };

  updateAnswerState = (answerState) => {
    this.setState({ answerState }, () => {
      if (answerState === "passed") {
        this.setState({
          showModal: true,
          answerStateTagline: this.answerStateTagline(),
          imageUrl: this.imageUrl(),
        });

        this.props.getAnswer();
      }
    });
  };

  updateEnrolment = (enrolment) => {
    this.setState({ enrolment });
  };

  handleHideAnswerStateModal = () => {
    this.setState({ showModal: false });
  };

  fetchComments = () => {
    return getRequest(`/api/comments.json?question_id=${this.props.lessonId}`)
      .then((response: AxiosResponse) => {
        this.setState({
          comments: response.data,
          loadingComments: true,
        });
      })
      .catch((error) => {
        this.setState({
          loadingComments: false,
        });

        errorNotification(
          "Error loading comments",
          JSON.stringify(error.response.data),
        );
      });
  };

  fetchMoreData = () => {
    const { enrolmentId, answerId, lessonId } = this.props;
    const { page, sortBy, hasMore } = this.state;

    if (!hasMore) {
      return;
    }

    setTimeout(() => {
      return getRequest(
        `/api/enrolments/${enrolmentId}/questions/${lessonId}/answer/answer_upvotes?answer_id=${answerId}&sort_by=${sortBy}&page=${page}`,
      )
        .then((response: AxiosResponse) => {
          this.setState({
            answers: response.data.answers,
            chosen: response.data.chosen || [],
            loading: false,
            page: response.data.page,
            hasMore: response.data.has_more,
            prev: response.data.prev,
            next: response.data.next,
          });
        })
        .catch((error) => {
          this.setState({
            loading: false,
          });

          errorNotification(
            "Error loading lesson",
            JSON.stringify(error?.response?.data),
          );
        });
    }, 1500);
  };

  handleAnswersPagination = (page: number) => {
    this.setState({ page, loading: true }, () => this.fetchMoreData());
  };

  updateComments = () => {
    this.setState({ loading: false });
    this.fetchComments();
  };

  onClickSortOption = (e, option) => {
    e.preventDefault();

    const { sortBy } = this.state;

    if (sortBy === option) {
      return;
    }

    this.setState({ sortBy: option, loading: true, sortMenuOpen: false }, () =>
      this.fetchAnswers(option),
    );
  };

  toggleSortMenu = (e) => {
    e.preventDefault();
    this.setState({ sortMenuOpen: !this.state.sortMenuOpen });
  };

  handleDocumentClick = (e: MouseEvent) => {
    if (
      this.state.sortMenuOpen &&
      this.sortRef.current &&
      !this.sortRef.current.contains(e.target as Node)
    ) {
      this.setState({ sortMenuOpen: false });
    }
  };

  scrollToAnswerUpvotes = () => {
    window.setTimeout(() => {
      this.answerUpvotesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  sortLabel = () => {
    switch (this.state.sortBy) {
      case "votes":
        return "Most popular";
      case "created":
        return "Most recent";
      default:
        return "Random";
    }
  };

  answerStateTagline = () => {
    const { answerState } = this.state;

    if (!answerState) {
      return "";
    }

    switch (answerState) {
      case "pending":
        return "Please vote";
      case "submitted":
        return "We'll get back to you";
      case "passed":
        return passedTaglines[
          Math.floor(Math.random() * passedTaglines.length)
        ];
      case "failed":
        return failedTaglines[
          Math.floor(Math.random() * failedTaglines.length)
        ];
      default:
        return "Something went wrong";
    }
  };

  imageUrl = () => {
    const { answerState } = this.state;

    const { answer } = this.props;

    if (!answerState) {
      return "";
    }

    let base_url =
      "https://42courses-course-material.s3-us-west-1.amazonaws.com/answers/giphys";
    let flavors = answerState == "passed" ? this.range(70) : this.range(45);
    let flavor = answer
      ? answer.id % flavors.length
      : Math.floor(Math.random() * flavors.length);

    switch (answerState) {
      case "pending":
      case "submitted":
        return `${base_url}/team-pending.png`;
      case "passed":
        return `${base_url}/pass/passed-${flavor}.gif`;
      case "failed":
        return `${base_url}/fail/failed-${flavor}.gif`;
      default:
        return "Something went wrong";
    }
  };

  range = (max) => {
    return Array.from({ length: max }, (v, k) => k + 1);
  };

  updateUpvoteCount = (idx, count, newEnrolmentIds) => {
    let answers = [...this.state.answers];
    answers[idx] = {
      ...answers[idx],
      answer_upvotes_count: count,
      answer_upvotes_enrolment_ids: newEnrolmentIds,
    };
    this.setState({ answers });
  };

  updateUpvoteComments = (idx, updatedAnswer) => {
    let answers = [...this.state.answers];

    answers[idx] = {
      ...answers[idx],
      answer: updatedAnswer,
    };
    this.setState({ answers });
  };

  nextQuestionPath = () => {
    const { lesson, lastLesson, enrolment } = this.props;

    return nextLessonPath(lesson, lastLesson, enrolment);
  };

  render() {
    const {
      lesson,
      lastLesson,
      answer,
      enrolmentId,
      lessonId,
      answerId,
      page,
      refreshAnswers,
      getRecentNotifications,
      loadingLesson,
      loadingAnswer,
      getXp,
      currentUser,
      search,
    } = this.props;

    const {
      answers,
      loading,
      answerState,
      showModal,
      chosen,
      comments,
      hasMore,
      answerStateTagline,
      imageUrl,
      enrolment,
      prev,
      next,
      sortMenuOpen,
    } = this.state;
    // The picks come from the server, covering every page. The answers on
    // screen are then layered over that, so liking or unliking moves the
    // counter straight away rather than waiting for the next fetch.
    //
    // Counting only the current page -- which is what this did -- meant a
    // learner who had picked three, then came back to the lesson, saw however
    // many of their three happened to land on the page they were shown. The
    // list is ten at a time and randomly sorted, so that was usually one.
    const likedIds = new Set<number>(chosen.map((c) => c.id));
    answers.forEach((a) => {
      if (a.answer_upvotes_enrolment_ids?.includes(Number(enrolmentId))) {
        likedIds.add(a.id);
      } else {
        likedIds.delete(a.id);
      }
    });
    const selectedAnswersCount = likedIds.size;

    const passed = answerState === "passed" || answer?.state === "passed";

    const renderAnswer = (ans, i: number) => (
      <AnswerUpvote
        key={ans.id || i}
        idx={i}
        answer={ans}
        updateEnrolment={this.updateEnrolment}
        updateAnswerState={this.updateAnswerState}
        enrolmentId={enrolmentId}
        lessonId={lessonId}
        answerId={answerId}
        answerState={answerState}
        originalAnswer={answer}
        updateUpvoteCount={this.updateUpvoteCount}
        answerUpvoteCount={ans.answer_upvotes_count}
        answerUpvoteEnrolmentIds={ans.answer_upvotes_enrolment_ids}
        currentUser={currentUser}
        updateUpvoteComments={this.updateUpvoteComments}
        search={search}
      />
    );

    // Once the lesson is passed, the three the learner chose go above the rest
    // rather than being left somewhere in a randomly sorted, paginated list of
    // several hundred. Coming back to a passed lesson, this is the thing they
    // came back to see.
    const chosenSection = passed && chosen.length > 0 && (
      <section className="op-chosen" aria-labelledby="chosenHeading">
        <h3 className="op-chosen__title" id="chosenHeading">
          {chosen.length === 3 ? "The three you chose" : "The answers you chose"}
        </h3>
        {/* An answer can be withdrawn after it was picked -- its author spends
            a life and retakes the lesson, which cancels it. The pick stands,
            but there is nothing left to show, so say so rather than quietly
            listing two under a heading that says three. */}
        {chosen.length < 3 && (
          <p className="op-chosen__note">
            {3 - chosen.length === 1
              ? "One of the answers you picked has since been withdrawn by its author."
              : `${3 - chosen.length} of the answers you picked have since been withdrawn by their authors.`}{" "}
            Your pass is not affected.
          </p>
        )}
        <ul className="op-list op-list--chosen">
          {chosen.map((ans, i: number) => renderAnswer(ans, i))}
        </ul>
      </section>
    );

    if (this.props.embedded) {
      if (loadingLesson || loadingAnswer || !answer) {
        return (
          <div
            className="answer-upvotes-container q-card"
            ref={this.answerUpvotesRef}
          >
            <div className="ui active inverted dimmer">
              <div className="ui text loader">Loading</div>
            </div>
          </div>
        );
      }

      return (
        <>
          <div
            className="answer-upvotes-container q-card"
            ref={this.answerUpvotesRef}
          >
            <div className="q-row">
              <span className="q-badge">Q</span>
              <div
                className="q-text"
                dangerouslySetInnerHTML={{
                  __html: lesson?.formatted_description,
                }}
              />
            </div>
            <div className="q-row q-answer">
              <span className="q-badge a">A</span>
              <div className="q-answer__body">
                <p className="op-your">
                  {answer?.formatted_content ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: answer.formatted_content,
                      }}
                    />
                  ) : (
                    answer?.content_url
                  )}
                </p>
                {!(answerState === "passed" || answer?.state === "passed") && (
                  <p className="q-hint">
                    Submitted. Now like three answers below to complete the
                    lesson.
                  </p>
                )}
              </div>
            </div>

            {!passed && (
              <div className="op-bar">
                Select three answers below that you find interesting
              </div>
            )}

            {chosenSection}

            <div className="op-controls">
              {passed ? (
                <h3 className="op-controls__title">
                  Everyone else&rsquo;s answers
                </h3>
              ) : (
                <span className="op-count">
                  <b>{Math.min(selectedAnswersCount, 3)}</b> of 3 selected
                </span>
              )}
              <div className="op-sort" ref={this.sortRef}>
                <button
                  type="button"
                  className="op-sort__btn"
                  aria-haspopup="true"
                  aria-expanded={sortMenuOpen}
                  onClick={this.toggleSortMenu}
                >
                  Sort by {this.sortLabel()}
                  <svg aria-hidden="true" viewBox="0 0 12 12">
                    <path
                      d="M3 4.5 L6 7.5 L9 4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div
                  className="op-sort__menu"
                  role="menu"
                  hidden={!sortMenuOpen}
                >
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={this.state.sortBy === "random"}
                    onClick={(e) => this.onClickSortOption(e, "random")}
                  >
                    Random
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={this.state.sortBy === "votes"}
                    onClick={(e) => this.onClickSortOption(e, "votes")}
                  >
                    Most popular
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={this.state.sortBy === "created"}
                    onClick={(e) => this.onClickSortOption(e, "created")}
                  >
                    Most recent
                  </button>
                </div>
              </div>
            </div>

            <ul className="op-list">
              {loading ? (
                <li className="op-ans">
                  <div className="ui active inverted dimmer">
                    <div className="ui text loader">Loading</div>
                  </div>
                </li>
              ) : (
                answers.map((ans, i) => (
                  <AnswerUpvote
                    key={i}
                    idx={i}
                    answer={ans}
                    updateEnrolment={this.updateEnrolment}
                    updateAnswerState={this.updateAnswerState}
                    enrolmentId={enrolmentId}
                    lessonId={lessonId}
                    answerId={answerId}
                    answerState={answerState}
                    originalAnswer={answer}
                    updateUpvoteCount={this.updateUpvoteCount}
                    answerUpvoteCount={ans.answer_upvotes_count}
                    answerUpvoteEnrolmentIds={ans.answer_upvotes_enrolment_ids}
                    currentUser={currentUser}
                    updateUpvoteComments={this.updateUpvoteComments}
                    search={search}
                  />
                ))
              )}
            </ul>
            <div className="answer_upvotes--pagination-buttons">
              <button
                className="lesson-controls__btn"
                type="button"
                disabled={!prev || loading}
                onClick={() => this.handleAnswersPagination(prev)}
                aria-label="Previous answers"
              >
                <svg aria-hidden="true">
                  <use href="#i-chev-left" />
                </svg>
              </button>
              <button
                className="lesson-controls__btn"
                type="button"
                disabled={!next || loading}
                onClick={() => this.handleAnswersPagination(next)}
                aria-label="Next answers"
              >
                <svg aria-hidden="true">
                  <use href="#i-chev-right" />
                </svg>
              </button>
            </div>
          </div>
          {enrolment && answerState && (
            <AnswerStateModal
              answerId={answerId}
              enrolment={enrolment}
              showModal={showModal}
              answerState={answerState}
              lesson={lesson}
              answerStateTagline={answerStateTagline}
              imageUrl={imageUrl}
              nextPath={this.nextQuestionPath()}
              handleHideAnswerStateModal={this.handleHideAnswerStateModal}
            />
          )}
        </>
      );
    }

    return (
      <>
        {loadingLesson || loadingAnswer ? (
          <>
            <div className="ui active inverted dimmer">
              <div className="ui text loader">Loading</div>
            </div>
            <div className="hero">
              <div
                className="bg"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${lesson?.topic?.masthead_image_url})`,
                }}
              ></div>
            </div>
          </>
        ) : (
          <>
            <div
              className={`hero ${
                answerState === "passed" || answer?.state === "passed"
                  ? "passed"
                  : ""
              }`}
            >
              <div
                className="bg"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${lesson?.topic.masthead_image_url})`,
                }}
              ></div>
              <div className="course-header">
                <p>
                  Chapter {(lesson?.topic.position || 0) + 1} | Lesson{" "}
                  {(lesson?.position || 0) + 1}
                </p>
                <h1>{lesson?.name}</h1>
                <p className="minutes">{lesson?.length_minutes} min</p>
              </div>
            </div>
            <div className="ui segment" style={{ zIndex: 30 }}>
              {answerState === "passed" || answer?.state === "passed" ? (
                <div className="ui container current-lesson-container">
                  <div className="current-lesson">
                    <div className="ui text container lesson-text-target">
                      <div className="lesson-target" />
                      <div
                        dangerouslySetInnerHTML={{
                          __html: lesson?.formatted_foreword,
                        }}
                      />
                      <PersonalLessonSummary
                        enrolment={enrolment}
                        question={lesson}
                      />
                    </div>
                  </div>
                  <Assignment
                    mode={lesson?.kind}
                    enrolment={enrolment}
                    question={lesson}
                    lastLesson={lastLesson}
                    page={page}
                    answer={answer}
                    refreshAnswers={refreshAnswers}
                    loadingAnswer={loadingAnswer}
                    getRecentNotifications={getRecentNotifications}
                    getXp={getXp}
                    currentUser={currentUser}
                    search={search}
                  />

                  <>
                    <div
                      className="ui container answer-upvotes"
                      style={{ padding: "0px" }}
                    >
                      <div
                        className="answer-upvotes-container"
                        ref={this.answerUpvotesRef}
                        style={{ marginTop: "50px", width: "100%" }}
                      >
                        {chosenSection}

                        <div className="op-controls">
                          {passed ? (
                            <h3 className="op-controls__title">
                              Everyone else&rsquo;s answers
                            </h3>
                          ) : (
                            <span className="op-count">
                              <b>{selectedAnswersCount}</b> selected
                            </span>
                          )}
                          <div className="op-sort" ref={this.sortRef}>
                            <button
                              type="button"
                              className="op-sort__btn"
                              aria-haspopup="true"
                              aria-expanded={sortMenuOpen}
                              onClick={this.toggleSortMenu}
                            >
                              Sort by {this.sortLabel()}
                              <svg aria-hidden="true" viewBox="0 0 12 12">
                                <path
                                  d="M3 4.5 L6 7.5 L9 4.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              className="op-sort__menu"
                              role="menu"
                              hidden={!sortMenuOpen}
                            >
                              <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={this.state.sortBy === "random"}
                                onClick={(e) =>
                                  this.onClickSortOption(e, "random")
                                }
                              >
                                Random
                              </button>
                              <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={this.state.sortBy === "votes"}
                                onClick={(e) =>
                                  this.onClickSortOption(e, "votes")
                                }
                              >
                                Most popular
                              </button>
                              <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={this.state.sortBy === "created"}
                                onClick={(e) =>
                                  this.onClickSortOption(e, "created")
                                }
                              >
                                Most recent
                              </button>
                            </div>
                          </div>
                        </div>

                        <ul className="op-list">
                          {loading ? (
                            <li className="op-ans">
                              <div className="ui active inverted dimmer">
                                <div className="ui text loader">Loading</div>
                              </div>
                            </li>
                          ) : (
                            answers.map((ans, i: number) => (
                              <AnswerUpvote
                                key={i}
                                idx={i}
                                answer={ans}
                                updateAnswerState={this.updateAnswerState}
                                updateEnrolment={this.updateEnrolment}
                                enrolmentId={enrolmentId}
                                lessonId={lessonId}
                                answerId={answerId}
                                answerState={answerState}
                                originalAnswer={answer}
                                updateUpvoteCount={this.updateUpvoteCount}
                                answerUpvoteCount={ans.answer_upvotes_count}
                                answerUpvoteEnrolmentIds={
                                  ans.answer_upvotes_enrolment_ids
                                }
                                currentUser={currentUser}
                                updateUpvoteComments={this.updateUpvoteComments}
                                search={search}
                              />
                            ))
                          )}
                        </ul>
                        <div className="answer_upvotes--pagination-buttons">
                          <button
                            className="lesson-controls__btn"
                            type="button"
                            disabled={!prev || loading}
                            onClick={() => this.handleAnswersPagination(prev)}
                            aria-label="Previous answers"
                          >
                            <svg aria-hidden="true">
                              <use href="#i-chev-left" />
                            </svg>
                          </button>
                          <button
                            className="lesson-controls__btn"
                            type="button"
                            disabled={!next || loading}
                            onClick={() => this.handleAnswersPagination(next)}
                            aria-label="Next answers"
                          >
                            <svg aria-hidden="true">
                              <use href="#i-chev-right" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <Discussion
                      updateComments={this.updateComments}
                      lessonId={lessonId}
                      currentUser={currentUser}
                    />
                    <Conversations
                      comments={comments}
                      loaded={!loading}
                      questionId={lessonId}
                      currentUser={currentUser}
                      enrolmentId={enrolmentId}
                      updateComments={this.updateComments}
                    />
                  </>
                </div>
              ) : (
                <div className="ui container answer-upvotes">
                  <div className="answer-upvotes-wrapper">
                    <h3>Question</h3>
                  </div>
                  <div
                    className="answer-upvotes-container q-card"
                    ref={this.answerUpvotesRef}
                  >
                    <div className="q-row">
                      <span className="q-badge">Q</span>
                      <div
                        className="q-text"
                        dangerouslySetInnerHTML={{
                          __html: lesson.formatted_description,
                        }}
                      />
                    </div>
                    <div className="q-row q-answer">
                      <span className="q-badge a">A</span>
                      <div className="q-answer__body">
                        <p className="op-your">
                          {answer?.formatted_content ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: answer.formatted_content,
                              }}
                            />
                          ) : (
                            answer?.content_url
                          )}
                        </p>
                        <p className="q-hint">
                          Submitted. Now like three answers below to complete
                          the lesson.
                        </p>
                      </div>
                    </div>

                    {!passed && (
                      <div className="op-bar">
                        Select three answers below that you find interesting
                      </div>
                    )}

                    {chosenSection}

                    <div className="op-controls">
                      {passed ? (
                        <h3 className="op-controls__title">
                          Everyone else&rsquo;s answers
                        </h3>
                      ) : (
                        <span className="op-count">
                          <b>{Math.min(selectedAnswersCount, 3)}</b> of 3
                          selected
                        </span>
                      )}
                      <div className="op-sort" ref={this.sortRef}>
                        <button
                          type="button"
                          className="op-sort__btn"
                          aria-haspopup="true"
                          aria-expanded={sortMenuOpen}
                          onClick={this.toggleSortMenu}
                        >
                          Sort by {this.sortLabel()}
                          <svg aria-hidden="true" viewBox="0 0 12 12">
                            <path
                              d="M3 4.5 L6 7.5 L9 4.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div
                          className="op-sort__menu"
                          role="menu"
                          hidden={!sortMenuOpen}
                        >
                          <button
                            type="button"
                            role="menuitemradio"
                            aria-checked={this.state.sortBy === "random"}
                            onClick={(e) => this.onClickSortOption(e, "random")}
                          >
                            Random
                          </button>
                          <button
                            type="button"
                            role="menuitemradio"
                            aria-checked={this.state.sortBy === "votes"}
                            onClick={(e) => this.onClickSortOption(e, "votes")}
                          >
                            Most popular
                          </button>
                          <button
                            type="button"
                            role="menuitemradio"
                            aria-checked={this.state.sortBy === "created"}
                            onClick={(e) =>
                              this.onClickSortOption(e, "created")
                            }
                          >
                            Most recent
                          </button>
                        </div>
                      </div>
                    </div>
                    <ul className="op-list">
                      {loading ? (
                        <li className="op-ans">
                          <div className="ui active inverted dimmer">
                            <div className="ui text loader">Loading</div>
                          </div>
                        </li>
                      ) : (
                        <>
                          {answers.map((ans, i) => (
                            <AnswerUpvote
                              key={i}
                              idx={i}
                              answer={ans}
                              updateEnrolment={this.updateEnrolment}
                              updateAnswerState={this.updateAnswerState}
                              enrolmentId={enrolmentId}
                              lessonId={lessonId}
                              answerId={answerId}
                              answerState={answerState}
                              originalAnswer={answer}
                              updateUpvoteCount={this.updateUpvoteCount}
                              answerUpvoteCount={ans.answer_upvotes_count}
                              answerUpvoteEnrolmentIds={
                                ans.answer_upvotes_enrolment_ids
                              }
                              currentUser={currentUser}
                              updateUpvoteComments={this.updateUpvoteComments}
                              search={search}
                            />
                          ))}
                        </>
                      )}
                    </ul>
                    <div className="answer_upvotes--pagination-buttons">
                      <button
                        className="lesson-controls__btn"
                        type="button"
                        disabled={!prev || loading}
                        onClick={() => this.handleAnswersPagination(prev)}
                        aria-label="Previous answers"
                      >
                        <svg aria-hidden="true">
                          <use href="#i-chev-left" />
                        </svg>
                      </button>
                      <button
                        className="lesson-controls__btn"
                        type="button"
                        disabled={!next || loading}
                        onClick={() => this.handleAnswersPagination(next)}
                        aria-label="Next answers"
                      >
                        <svg aria-hidden="true">
                          <use href="#i-chev-right" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <nav className="l-nav" aria-label="Lesson navigation">
                <Link
                  className="btn btn-outline btn--compact"
                  to={lesson?.previous_question_path}
                >
                  <svg aria-hidden="true">
                    <use href="#i-chev-left" />
                  </svg>
                  Prev
                </Link>
                <Link
                  className="btn btn-blue btn--compact"
                  to={this.nextQuestionPath()}
                >
                  Next
                  <svg aria-hidden="true">
                    <use href="#i-chev-right" />
                  </svg>
                </Link>
              </nav>
            </div>
            {enrolment && answerState && (
              <AnswerStateModal
                answerId={answerId}
                enrolment={enrolment}
                showModal={showModal}
                answerState={answerState}
                lesson={lesson}
                answerStateTagline={answerStateTagline}
                imageUrl={imageUrl}
                nextPath={this.nextQuestionPath()}
                handleHideAnswerStateModal={this.handleHideAnswerStateModal}
              />
            )}
          </>
        )}
      </>
    );
  }
}

export default AnswerUpvotes;
